import { graphql, useFragment } from 'react-relay';
import type {
  TimerContentRightDetailsFragment$key,
  TimerContentRightDetailsFragment$data,
} from './__generated__/TimerContentRightDetailsFragment.graphql';
import type { AllColorTokens } from '@vitvio/design-system';
import { Flex, Text, useTheme, VStack } from '@vitvio/design-system';
import { useUserContext } from '@vitvio/user-context';
import { Time, useTimeProviderContext } from '@vitvio/time-components';
import { getDifference, toZonedTime, parseISOSafe } from '@vitvio/helpers';
import { ClockIconBorderless, TimerIconBorderless, NotificationIconBorderless } from '@vitvio/shared-components';
import { getSortedEstimates } from './estimates-helpers/getSortedEstimates';
import { capitalize } from 'lodash';
import { useTranslation, type TFunction } from '@vitvio/locales';

const DEFAULT_TIMEZONE = 'UTC';

const fragment = graphql`
  fragment TimerContentRightDetailsFragment on Job {
    status
    durations {
      dynamicDurationMinutes
      plannedDurationMinutes
    }
    actualStartTime
    actualStopTime
    estimatedStartTime
    estimatedStopTime
    plannedStart
    plannedStop
    estimates(first: 20) {
      __id
      edges {
        node {
          id
          estimate
          insertedAt
        }
      }
    }
  }
`;
interface Props {
  category: 'SURGERY' | 'TURNOVER';
  fragmentRef: TimerContentRightDetailsFragment$key | undefined;
}

export const itemGap = [0, 2, 4, 6, 8, 10]; // Chakra UI responsive array
export function TimerContentRightDetails({ category, fragmentRef }: Props) {
  const { hospitalTimezone } = useUserContext();
  const activeTimezone = hospitalTimezone || DEFAULT_TIMEZONE;
  const { currentTimeInTimezone } = useTimeProviderContext();
  const data = useFragment(fragment, fragmentRef);
  const { getColor, getSpacing } = useTheme();
  const { t } = useTranslation();

  const estimatesSorted = getSortedEstimates(data?.estimates?.edges);
  const lastEstimateDayjs = parseISOSafe(estimatesSorted[0]?.insertedAt);

  const zonedTime = toZonedTime(lastEstimateDayjs, activeTimezone);
  const minsSinceLastUpdate = getDifference(zonedTime, currentTimeInTimezone, 'minutes');

  return (
    <Flex justifyContent={'space-between'} gap={itemGap} fontSize={'14px'} fontWeight={'500'}>
      <VStack align="start" spacing="6px">
        {getLeftTextsAndIcons(data?.status || '', category, getColor).map(({ text, icon }) => (
          <Text display={'flex'} gap={getSpacing('3')} key={text}>
            {icon}
            {text}
          </Text>
        ))}
      </VStack>
      <VStack align="end" spacing="6px">
        {getRightText(data, minsSinceLastUpdate, category, t)}
      </VStack>
    </Flex>
  );
}

function getTimeSinceLastUpdate(minsSinceLastUpdate: number | null, t: TFunction) {
  if (minsSinceLastUpdate == null || Number.isNaN(minsSinceLastUpdate)) {
    return 'Update pending';
  }
  // Math.max(0, ...) handles cases where client time lags slightly behind server time (negative difference)
  const time = Math.max(0, Math.floor(minsSinceLastUpdate));
  return t('common:time.minutesAgo', { time });
}

function renderTimeSinceLastUpdate(minsSinceLastUpdate: number | null, category: 'SURGERY' | 'TURNOVER', t: TFunction) {
  if (category === 'TURNOVER') return null;
  return <Text>{getTimeSinceLastUpdate(minsSinceLastUpdate, t)}</Text>;
}

function getRightText(
  data: TimerContentRightDetailsFragment$data | null | undefined,
  minsSinceLastUpdate: number | null,
  category: 'SURGERY' | 'TURNOVER',
  t: TFunction,
) {
  if (data?.status === 'COMPLETED') {
    return (
      <>
        <Time date={data?.actualStartTime} font="S_Medium" />
        <Time date={data?.actualStopTime} font="S_Medium" />
        <Text>{data?.durations?.dynamicDurationMinutes} min</Text>
      </>
    );
  }

  if (data?.status === 'RUNNING') {
    return (
      <>
        <Text>{data?.durations?.dynamicDurationMinutes} min</Text>
        <Text>{data?.durations?.plannedDurationMinutes} min</Text>
        {renderTimeSinceLastUpdate(minsSinceLastUpdate, category, t)}
      </>
    );
  }

  return (
    <>
      <Time date={data?.estimatedStartTime || data?.plannedStart} font="S_Medium" />
      <Time date={data?.estimatedStopTime || data?.plannedStop} font="S_Medium" />
      {renderTimeSinceLastUpdate(minsSinceLastUpdate, category, t)}
    </>
  );
}

function getLeftTextsAndIcons(
  status: string,
  category: 'SURGERY' | 'TURNOVER',
  getColor: (color: AllColorTokens) => string,
): { text: string; icon: React.ReactNode }[] {
  const parsedCategory = capitalize(category);

  if (status === 'COMPLETED') {
    return [
      {
        icon: <ClockIconBorderless color={getColor('fg.muted')} my={'auto'} />,
        text: `${parsedCategory} started at`,
      },
      {
        icon: <ClockIconBorderless color={getColor('fg.muted')} my={'auto'} />,
        text: `${parsedCategory} ended at`,
      },
      {
        icon: <TimerIconBorderless color={getColor('fg.muted')} my={'auto'} />,
        text: `Actual ${category?.toLowerCase()} duration`,
      },
    ];
  }

  if (status === 'RUNNING') {
    const items = [
      {
        icon: <ClockIconBorderless color={getColor('fg.accent')} my={'auto'} />,
        text: `Estimated ${category?.toLowerCase()} duration`,
      },
      {
        icon: <TimerIconBorderless color={getColor('fg.accent')} my={'auto'} />,
        text: `Planned ${category?.toLowerCase()} duration`,
      },
    ];

    if (category !== 'TURNOVER') {
      items.push({
        icon: <NotificationIconBorderless color={getColor('fg.accent')} my={'auto'} />,
        text: `Time since last update`,
      });
    }

    return items;
  }

  const items = [
    {
      icon: <ClockIconBorderless color={getColor('fg.accent')} my={'auto'} />,
      text: `Estimated start time`,
    },
    {
      icon: <ClockIconBorderless color={getColor('fg.accent')} my={'auto'} />,
      text: `Estimated end time`,
    },
  ];

  if (category !== 'TURNOVER') {
    items.push({
      icon: <NotificationIconBorderless color={getColor('fg.accent')} my={'auto'} />,
      text: `Time since last update`,
    });
  }

  return items;
}
